import { Grid, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  IconButton,
  Icons,
  Link,
  MaterialPalette,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useHistory } from 'react-router';

import { sentryCaptureException } from '../../utilities';
import { REGEX_EMAIL } from '../../utilities/constants';

interface Props {
  attemptUserSignUp: (password: string) => Promise<string>;
  email: string;
  userName: string;
  updateEmailError: (emailError: string) => void;
}

const useStyles = makeStyles({
  root: {
    '& .signInLink': {
      fontWeight: 700,
      marginLeft: 4,
    },
    '& .signInLink:hover': {
      textDecoration: 'none',
    },
  },
  nextBtn: { width: '100%' },
  alignCenter: {
    textAlign: 'center',
  },
  spaceP: {
    margin: '0px 0px 10px 0px',
  },
  alreadyHaveAcc: {
    color: 'rgba(255, 255, 255, 0.6)',
    display: 'inline',
  },
  signUpButtonContainer: {
    marginTop: 24,
    marginBottom: 24,
    '& #signUpButton': {
      borderRadius: '4px',
    },
    '& #signUpButtonDisabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.48)',
      borderRadius: '4px',
    },
  },
  signUpButtonLabel: {
    color: MaterialPalette.common.black,
  },
});

const SignUpFlowPassword = forwardRef(
  ({ attemptUserSignUp, email, userName, updateEmailError }: Props, ref) => {
    const classes = useStyles();
    const history = useHistory();
    const [password, setPassword] = useState<string>('');
    const [passwordError, setPasswordError] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isSignUp, setIsSignUp] = useState<boolean>(false);
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
    const [policyMessage, setPolicyMessage] = useState<string>('');
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const handleChange = (event: React.FocusEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setPassword(value);
      if (
        (value.length < 8 ||
          !RegExp(/.*[a-z].*/).test(value) ||
          !RegExp(/.*[A-Z].*/).test(value) ||
          !RegExp(/.*\d.*/).test(value) ||
          !RegExp(/[$&+,:;=?@#|'<>.^*()%!-]/).test(value)) &&
        value.length > 0
      ) {
        setPolicyMessage(
          'The password should have minimum 8 characters, in that please include one uppercase, one lowercase, one numeric and one special character.',
        );
      } else {
        setPolicyMessage('');
      }
    };

    const handleClickShowPassword = () => {
      setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
      setShowConfirmPassword(!showConfirmPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    const handleConfirmNewPassword = (event: React.FocusEvent<HTMLInputElement>) => {
      setPasswordError('');
      const value = event.target.value;
      setConfirmNewPassword(value);
      if (password !== value) {
        setPasswordError('Password did not match');
      }
    };

    const resetFormValidation = () => {
      setPasswordError('');
    };

    const validateEmail = (emailId: string) => {
      return new RegExp(REGEX_EMAIL).test(emailId);
    };

    const validateForm = (): boolean => {
      let valid = true;
      if (password === '' || confirmNewPassword === '' || userName === '') {
        setPasswordError('User Name or Password Required.');
        valid = false;
      } else if (!validateEmail(email)) {
        updateEmailError('Email invalid');
        valid = false;
        setIsSignUp(false);
      }
      return valid;
    };

    const SignUpUser = async () => {
      resetFormValidation();
      if (!validateForm()) {
        return;
      }
      setIsSignUp(true);
      try {
        const result = await attemptUserSignUp(password);

        if (result !== '') {
          setPasswordError(result);
          setIsSignUp(false);
        }
      } catch (error) {
        console.error('~ file: SignUpFlowPassword.tsx ~ line 140 ~ SignUpUser ~', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(error, '~ file: SignUpFlowPassword.tsx ~ line 140 ~ SignUpUser ~');
      }
      setIsSignUp(false);
    };

    useImperativeHandle(ref, () => ({
      signUpFormSubmit() {
        SignUpUser();
      },
    }));

    return (
      <Grid container className={classes.root}>
        <Grid item xs={12} className={classes.spaceP}>
          <TextField
            size="small"
            noMinHeight
            id="Password"
            key="Password"
            name="Password"
            label="Password"
            onChange={handleChange}
            error={policyMessage !== ''}
            errorText={policyMessage || ' '}
            type={showPassword ? 'text' : 'password'}
            value={password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  tabIndex={-1}
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  data-testid="pwdIcon"
                >
                  {showPassword ? (
                    <Icons.EyeFeather strokeWidth={2} />
                  ) : (
                    <Icons.EyeOffFeather strokeWidth={2} />
                  )}
                </IconButton>
              </InputAdornment>
            }
            inputProps={{ 'data-testid': 'passwordField' }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="ConfirmPassword"
            key="ConfirmPassword"
            name="ConfirmPassword"
            noMinHeight
            label="Confirm Password"
            onChange={handleConfirmNewPassword}
            onFocus={handleConfirmNewPassword}
            error={passwordError !== '' && confirmNewPassword !== ''}
            errorText={passwordError}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmNewPassword}
            size="small"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  tabIndex={-1}
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownConfirmPassword}
                  edge="end"
                  data-testid="confirmPwdIcon"
                >
                  {showConfirmPassword ? (
                    <Icons.EyeFeather strokeWidth={2} />
                  ) : (
                    <Icons.EyeOffFeather strokeWidth={2} />
                  )}
                </IconButton>
              </InputAdornment>
            }
            inputProps={{ 'data-testid': 'confirmpasswordField' }}
          />
        </Grid>

        <Grid
          item
          container
          xs={12}
          justifyContent="center"
          className={classes.signUpButtonContainer}
        >
          <div className={classes.nextBtn}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={SignUpUser}
              disabled={
                !userName ||
                !email ||
                !password ||
                !confirmNewPassword ||
                policyMessage !== '' ||
                passwordError !== ''
              }
              id={
                !userName ||
                !email ||
                !password ||
                !confirmNewPassword ||
                policyMessage !== '' ||
                passwordError !== ''
                  ? 'signUpButtonDisabled'
                  : 'signUpButton'
              }
              isLoading={isSignUp}
              data-cy="SignUpAsUser"
              noMinHeight
              data-testid="signupbutton"
            >
              <Typography fontWeight={700} className={classes.signUpButtonLabel}>
                Sign Up
              </Typography>
            </Button>
          </div>
        </Grid>

        <Grid item container xs={12} md={12} justifyContent="center" alignItems="baseline">
          <Typography className={classes.alreadyHaveAcc} fontWeight={700}>
            Already have an account?
          </Typography>
          <Link
            to=""
            onClick={() => history.push('/login')}
            color="primary"
            className="signInLink"
            data-testid="signinLink"
          >
            Sign in
          </Link>
        </Grid>
      </Grid>
    );
  },
);

export default SignUpFlowPassword;
